import type { EventSummary } from '../event/event';

export type GuestJourneyPhase =
  | 'first_entry'
  | 'identified_pending_response'
  | 'responded_active'
  | 'attending_upcoming'
  | 'post_event';

export type GuestJourneyTarget =
  | 'home'
  | 'events'
  | 'rsvp'
  | 'forms'
  | 'registry'
  | 'schedule'
  | 'seating'
  | 'gallery'
  | 'shared_planning'
  | 'access';

export type GuestTaskType =
  | 'onboarding'
  | 'rsvp'
  | 'form'
  | 'registry'
  | 'timeline'
  | 'seating'
  | 'gallery'
  | 'shared_planning'
  | 'post_event';

export type GuestTaskStatus =
  'complete' | 'in_progress' | 'not_started' | 'blocked';

export type GuestOnboardingStatus =
  'complete' | 'review_recommended' | 'action_required';

export type GuestAccessLifecycleStatus =
  'active' | 'expiring_soon' | 'expired' | 'revoked';

export interface GuestJourneySummaryRecord {
  phase: GuestJourneyPhase;
  summary: string;
  recommendedTarget: GuestJourneyTarget;
  recommendedLabel: string;
  attentionTarget: GuestJourneyTarget | null;
  availableTargets: GuestJourneyTarget[];
  returnTarget: GuestJourneyTarget;
  visibleEventCount: number;
  visibleFormCount: number;
  invitedEventCount: number;
  pendingRsvpEventCount: number;
  pendingFormCount: number;
  registryItemCount?: number;
  visibleScheduleCount: number;
  visibleSeatingCount: number;
  galleryItemCount: number;
  galleryUploadAllowed: boolean;
  sharedPlanningItemCount: number;
}

export interface GuestJourneyInput {
  events: Array<Pick<EventSummary, 'startsAt' | 'endsAt'>>;
  visibleFormCount: number;
  invitedEventCount: number;
  pendingRsvpEventCount: number;
  pendingFormCount: number;
  registryItemCount?: number;
  visibleScheduleCount: number;
  visibleSeatingCount: number;
  galleryItemCount: number;
  galleryUploadAllowed: boolean;
  sharedPlanningItemCount: number;
  now?: Date;
}

export interface GuestJourneySessionContext {
  actorId: string | null;
  occasionId: string;
  activeEventIds: string[];
  roleView: 'guest';
  accessMode: 'guest_token' | 'claimed_guest' | 'public_visitor';
  responseScope: 'actor' | 'household' | 'group' | 'delegated';
  responseUnitActorId: string | null;
  responseUnitDisplayName: string | null;
}

export type GuestJourneySessionRecord = GuestJourneySessionContext;

export interface GuestOnboardingSummaryRecord {
  status: GuestOnboardingStatus;
  summary: string;
  responseScope: 'actor' | 'household' | 'group' | 'delegated';
  participantCount: number;
  delegateCount: number;
  householdCount: number;
  localeCode: string;
}

export interface GuestAccessLifecycleRecord {
  status: GuestAccessLifecycleStatus;
  expiresAt: string | null;
  revokedAt: string | null;
  summary: string;
}

export interface GuestTaskRecord {
  type: GuestTaskType;
  status: GuestTaskStatus;
  required: boolean;
  target: GuestJourneyTarget;
  label: string;
  summary: string;
  metricLabel: string | null;
  metricValue: string | null;
}

export interface GuestDeepLinkResolution {
  target: GuestJourneyTarget;
  didFallback: boolean;
  reason:
    | 'requested'
    | 'recommended'
    | 'invalid_requested_target'
    | 'unavailable_requested_target';
}

function normalizeTarget(value: string | null | undefined) {
  switch (value?.trim().toLowerCase()) {
    case 'home':
    case 'overview':
      return 'home';
    case 'events':
      return 'events';
    case 'rsvp':
      return 'rsvp';
    case 'forms':
      return 'forms';
    case 'registry':
    case 'gifts':
      return 'registry';
    case 'schedule':
    case 'timeline':
      return 'schedule';
    case 'seating':
      return 'seating';
    case 'gallery':
      return 'gallery';
    case 'shared-planning':
    case 'shared_planning':
      return 'shared_planning';
    case 'access':
    case 'help':
      return 'access';
    default:
      return null;
  }
}

function normalizeCount(value: number | undefined, max?: number) {
  const count = Number.isFinite(value) ? Math.floor(value ?? 0) : 0;
  const normalized = Math.max(count, 0);

  return max === undefined ? normalized : Math.min(normalized, max);
}

function normalizeNow(value: Date | undefined) {
  return value && Number.isFinite(value.getTime()) ? value : new Date();
}

function normalizeRecoveryHref(value: string | null) {
  const trimmed = value?.trim() ?? '';

  if (!trimmed) {
    return null;
  }

  if (trimmed.startsWith('/') || /^https?:\/\//iu.test(trimmed)) {
    return trimmed;
  }

  return null;
}

function normalizeGuestJourneyInput(
  input: GuestJourneyInput,
): GuestJourneyInput {
  const invitedEventCount = normalizeCount(input.invitedEventCount);
  const visibleFormCount = normalizeCount(input.visibleFormCount);

  return {
    ...input,
    visibleFormCount,
    invitedEventCount,
    pendingRsvpEventCount: normalizeCount(
      input.pendingRsvpEventCount,
      invitedEventCount,
    ),
    pendingFormCount: normalizeCount(input.pendingFormCount, visibleFormCount),
    registryItemCount: normalizeCount(input.registryItemCount ?? 0),
    visibleScheduleCount: normalizeCount(input.visibleScheduleCount),
    visibleSeatingCount: normalizeCount(input.visibleSeatingCount),
    galleryItemCount: normalizeCount(input.galleryItemCount),
    sharedPlanningItemCount: normalizeCount(input.sharedPlanningItemCount),
  };
}

function hasUpcomingVisibleEvents(
  events: Array<Pick<EventSummary, 'startsAt' | 'endsAt'>>,
  now: Date,
) {
  const nowValue = now.getTime();

  return events.some((event) => {
    const comparisonValue = event.endsAt ?? event.startsAt;

    if (!comparisonValue) {
      return false;
    }

    const timestamp = new Date(comparisonValue).getTime();

    return !Number.isNaN(timestamp) && timestamp >= nowValue;
  });
}

function areVisibleEventsPast(
  events: Array<Pick<EventSummary, 'startsAt' | 'endsAt'>>,
  now: Date,
) {
  if (events.length === 0) {
    return false;
  }

  const nowValue = now.getTime();

  return events.every((event) => {
    const comparisonValue = event.endsAt ?? event.startsAt;

    if (!comparisonValue) {
      return false;
    }

    const timestamp = new Date(comparisonValue).getTime();

    return !Number.isNaN(timestamp) && timestamp < nowValue;
  });
}

function getAvailableTargets(input: GuestJourneyInput): GuestJourneyTarget[] {
  const targets = new Set<GuestJourneyTarget>(['home', 'access']);

  if (input.events.length > 0) {
    targets.add('events');
  }

  if (input.invitedEventCount > 0) {
    targets.add('rsvp');
  }

  if (input.visibleFormCount > 0) {
    targets.add('forms');
  }

  if ((input.registryItemCount ?? 0) > 0) {
    targets.add('registry');
  }

  if (input.visibleScheduleCount > 0) {
    targets.add('schedule');
  }

  if (input.visibleSeatingCount > 0) {
    targets.add('seating');
  }

  if (input.galleryItemCount > 0 || input.galleryUploadAllowed) {
    targets.add('gallery');
  }

  if (input.sharedPlanningItemCount > 0) {
    targets.add('shared_planning');
  }

  return Array.from(targets);
}

export function toGuestJourneyPath(target: GuestJourneyTarget) {
  switch (target) {
    case 'home':
      return '/';
    case 'events':
      return '/events';
    case 'rsvp':
      return '/rsvp';
    case 'forms':
      return '/forms';
    case 'registry':
      return '/registry';
    case 'schedule':
      return '/schedule';
    case 'seating':
      return '/seating';
    case 'gallery':
      return '/gallery';
    case 'shared_planning':
      return '/shared-planning';
    case 'access':
      return '/access';
  }
}

export function resolveGuestJourneySummary(
  input: GuestJourneyInput,
): GuestJourneySummaryRecord {
  const normalizedInput = normalizeGuestJourneyInput(input);
  const now = normalizeNow(normalizedInput.now);
  const availableTargets = getAvailableTargets(normalizedInput);
  const upcoming = hasUpcomingVisibleEvents(normalizedInput.events, now);
  const postEvent = areVisibleEventsPast(normalizedInput.events, now);

  let phase: GuestJourneyPhase = 'first_entry';
  let recommendedTarget: GuestJourneyTarget = 'access';
  let recommendedLabel = 'Review your access';
  let summary =
    'Enter the occasion safely, confirm the invite context, and wait for hosts to publish the next visible guest step.';

  if (normalizedInput.pendingRsvpEventCount > 0) {
    phase = 'identified_pending_response';
    recommendedTarget = 'rsvp';
    recommendedLabel = 'Finish RSVP';
    summary =
      'This guest still has RSVP work to complete before the occasion flow is fully settled.';
  } else if (normalizedInput.pendingFormCount > 0) {
    phase = 'identified_pending_response';
    recommendedTarget = 'forms';
    recommendedLabel = 'Complete forms';
    summary =
      'RSVP is in a stable state, but the hosts still need more guest information.';
  } else if (postEvent) {
    phase = 'post_event';
    recommendedTarget =
      normalizedInput.galleryItemCount > 0 ||
      normalizedInput.galleryUploadAllowed
        ? 'gallery'
        : normalizedInput.events.length > 0
          ? 'events'
          : 'home';
    recommendedLabel =
      recommendedTarget === 'gallery' ? 'Return to gallery' : 'Open overview';
    summary =
      'The primary event window has passed, so the experience shifts toward return visits and approved post-event content.';
  } else if (upcoming && normalizedInput.invitedEventCount > 0) {
    phase = 'attending_upcoming';
    recommendedTarget =
      normalizedInput.visibleScheduleCount > 0
        ? 'schedule'
        : normalizedInput.visibleSeatingCount > 0
          ? 'seating'
          : 'events';
    recommendedLabel =
      recommendedTarget === 'schedule'
        ? 'Open schedule'
        : recommendedTarget === 'seating'
          ? 'Review seating'
          : 'Browse events';
    summary =
      'Core participation tasks are complete, so the guest experience shifts into arrival, schedule, and event-day coordination.';
  } else if (
    normalizedInput.invitedEventCount > 0 ||
    normalizedInput.visibleScheduleCount > 0 ||
    normalizedInput.visibleSeatingCount > 0 ||
    normalizedInput.galleryItemCount > 0
  ) {
    phase = 'responded_active';
    recommendedTarget =
      normalizedInput.visibleScheduleCount > 0
        ? 'schedule'
        : normalizedInput.events.length > 0
          ? 'events'
          : normalizedInput.galleryItemCount > 0 ||
              normalizedInput.galleryUploadAllowed
            ? 'gallery'
            : 'home';
    recommendedLabel =
      recommendedTarget === 'schedule'
        ? 'Open schedule'
        : recommendedTarget === 'gallery'
          ? 'Open gallery'
          : 'Browse events';
    summary =
      'The guest has already moved through the primary invite step and can return later without losing context.';
  }

  return {
    phase,
    summary,
    recommendedTarget,
    recommendedLabel,
    attentionTarget:
      normalizedInput.pendingRsvpEventCount > 0
        ? 'rsvp'
        : normalizedInput.pendingFormCount > 0
          ? 'forms'
          : null,
    availableTargets,
    returnTarget:
      normalizedInput.visibleScheduleCount > 0
        ? 'schedule'
        : normalizedInput.galleryItemCount > 0 ||
            normalizedInput.galleryUploadAllowed
          ? 'gallery'
          : recommendedTarget,
    visibleEventCount: normalizedInput.events.length,
    visibleFormCount: normalizedInput.visibleFormCount,
    invitedEventCount: normalizedInput.invitedEventCount,
    pendingRsvpEventCount: normalizedInput.pendingRsvpEventCount,
    pendingFormCount: normalizedInput.pendingFormCount,
    registryItemCount: normalizedInput.registryItemCount ?? 0,
    visibleScheduleCount: normalizedInput.visibleScheduleCount,
    visibleSeatingCount: normalizedInput.visibleSeatingCount,
    galleryItemCount: normalizedInput.galleryItemCount,
    galleryUploadAllowed: normalizedInput.galleryUploadAllowed,
    sharedPlanningItemCount: normalizedInput.sharedPlanningItemCount,
  };
}

export function resolveGuestOnboardingSummary(input: {
  accessMode: 'guest_token' | 'claimed_guest' | 'public_visitor';
  responseScope: 'actor' | 'household' | 'group' | 'delegated';
  participantCount: number;
  delegateCount: number;
  householdCount: number;
  localeCode: string;
  phase: GuestJourneyPhase;
}): GuestOnboardingSummaryRecord {
  let status: GuestOnboardingStatus = 'complete';
  let summary = `Guest access is localized to ${input.localeCode} and ready for the next visible occasion step.`;

  if (input.phase === 'first_entry') {
    status = 'action_required';
    summary =
      input.responseScope === 'actor'
        ? 'Confirm the invite details, preferred language, and event context before moving deeper into the occasion.'
        : 'Review who this invite covers, confirm the shared response context, and continue with the guest tasks that follow.';
  } else if (
    input.responseScope !== 'actor' ||
    input.delegateCount > 0 ||
    input.householdCount > 0 ||
    input.accessMode === 'guest_token'
  ) {
    status = 'review_recommended';
    summary =
      input.responseScope === 'actor'
        ? 'This invite stays simple, but access details remain available if the guest needs to confirm language, identity, or return continuity.'
        : 'This invite spans a shared household or delegated response unit, so access details stay important even after the first entry.';
  }

  return {
    status,
    summary,
    responseScope: input.responseScope,
    participantCount: input.participantCount,
    delegateCount: input.delegateCount,
    householdCount: input.householdCount,
    localeCode: input.localeCode,
  };
}

export function resolveGuestAccessLifecycle(input: {
  expiresAt: string | null;
  revokedAt: string | null;
  now?: Date;
}): GuestAccessLifecycleRecord {
  const now = normalizeNow(input.now);

  if (input.revokedAt) {
    return {
      status: 'revoked',
      expiresAt: input.expiresAt,
      revokedAt: input.revokedAt,
      summary:
        'This invite has been revoked. The guest should reopen the latest trusted link from the host.',
    };
  }

  if (input.expiresAt) {
    const expiresAt = new Date(input.expiresAt);

    if (!Number.isNaN(expiresAt.getTime())) {
      if (expiresAt.getTime() <= now.getTime()) {
        return {
          status: 'expired',
          expiresAt: input.expiresAt,
          revokedAt: null,
          summary:
            'This invite has expired. The guest should request a fresh access link.',
        };
      }

      const hoursRemaining = Math.floor(
        (expiresAt.getTime() - now.getTime()) / (60 * 60 * 1000),
      );

      if (hoursRemaining <= 72) {
        return {
          status: 'expiring_soon',
          expiresAt: input.expiresAt,
          revokedAt: null,
          summary:
            'This invite is still active but nearing expiry, so return continuity should stay low-friction.',
        };
      }
    }
  }

  return {
    status: 'active',
    expiresAt: input.expiresAt,
    revokedAt: input.revokedAt,
    summary:
      'This invite is active and can continue to anchor return visits on the same occasion.',
  };
}

export function resolveGuestTaskList(input: {
  summary: GuestJourneySummaryRecord;
  onboarding: GuestOnboardingSummaryRecord;
}): GuestTaskRecord[] {
  const tasks: GuestTaskRecord[] = [
    {
      type: 'onboarding',
      status:
        input.onboarding.status === 'complete'
          ? 'complete'
          : input.onboarding.status === 'action_required'
            ? 'in_progress'
            : 'not_started',
      required: input.onboarding.status !== 'complete',
      target: 'access',
      label:
        input.onboarding.status === 'action_required'
          ? 'Review access'
          : 'Access details',
      summary: input.onboarding.summary,
      metricLabel: 'response scope',
      metricValue: input.onboarding.responseScope.replace('_', ' '),
    },
  ];

  if (input.summary.invitedEventCount > 0) {
    tasks.push({
      type: 'rsvp',
      status:
        input.summary.pendingRsvpEventCount === 0
          ? 'complete'
          : input.summary.pendingRsvpEventCount ===
              input.summary.invitedEventCount
            ? 'not_started'
            : 'in_progress',
      required: true,
      target: 'rsvp',
      label:
        input.summary.pendingRsvpEventCount > 0 ? 'Finish RSVP' : 'Review RSVP',
      summary:
        input.summary.pendingRsvpEventCount > 0
          ? 'Attendance is still missing for one or more invited events.'
          : 'Attendance has been captured for the invited events currently visible to this guest.',
      metricLabel: 'events pending',
      metricValue: `${input.summary.pendingRsvpEventCount}/${input.summary.invitedEventCount}`,
    });
  }

  if (input.summary.visibleFormCount > 0) {
    tasks.push({
      type: 'form',
      status:
        input.summary.pendingFormCount === 0
          ? 'complete'
          : input.summary.pendingFormCount === input.summary.visibleFormCount
            ? 'not_started'
            : 'in_progress',
      required: input.summary.pendingFormCount > 0,
      target: 'forms',
      label:
        input.summary.pendingFormCount > 0 ? 'Complete forms' : 'Review forms',
      summary:
        input.summary.pendingFormCount > 0
          ? 'Guest questions remain open and should be completed from the same occasion flow.'
          : 'The currently visible guest forms have already been completed.',
      metricLabel: 'forms pending',
      metricValue: `${input.summary.pendingFormCount}/${input.summary.visibleFormCount}`,
    });
  }

  if ((input.summary.registryItemCount ?? 0) > 0) {
    tasks.push({
      type: 'registry',
      status: 'complete',
      required: false,
      target: 'registry',
      label: 'Registry',
      summary:
        'Registry items are visible for this guest and can be claimed from the same occasion flow.',
      metricLabel: 'visible items',
      metricValue: String(input.summary.registryItemCount ?? 0),
    });
  }

  tasks.push({
    type: 'timeline',
    status:
      input.summary.visibleScheduleCount > 0
        ? 'complete'
        : input.summary.invitedEventCount > 0
          ? 'in_progress'
          : 'blocked',
    required: false,
    target: 'schedule',
    label:
      input.summary.visibleScheduleCount > 0
        ? 'Open schedule'
        : 'Schedule pending',
    summary:
      input.summary.visibleScheduleCount > 0
        ? 'Guest-safe timing and logistics are available for the visible occasion flow.'
        : 'Schedule details have not been published for this guest context yet.',
    metricLabel: 'visible items',
    metricValue: String(input.summary.visibleScheduleCount),
  });

  tasks.push({
    type: 'seating',
    status:
      input.summary.visibleSeatingCount > 0
        ? 'complete'
        : input.summary.phase === 'attending_upcoming'
          ? 'in_progress'
          : 'blocked',
    required: false,
    target: 'seating',
    label:
      input.summary.visibleSeatingCount > 0
        ? 'Review seating'
        : 'Seating pending',
    summary:
      input.summary.visibleSeatingCount > 0
        ? 'Published seating assignments are visible for this guest.'
        : 'Seating stays hidden until hosts deliberately publish it for this invite context.',
    metricLabel: 'published entries',
    metricValue: String(input.summary.visibleSeatingCount),
  });

  tasks.push({
    type: 'gallery',
    status:
      input.summary.galleryItemCount > 0 || input.summary.galleryUploadAllowed
        ? 'complete'
        : input.summary.phase === 'post_event'
          ? 'in_progress'
          : 'blocked',
    required: false,
    target: 'gallery',
    label:
      input.summary.galleryItemCount > 0 || input.summary.galleryUploadAllowed
        ? 'Open gallery'
        : 'Gallery pending',
    summary:
      input.summary.galleryItemCount > 0 || input.summary.galleryUploadAllowed
        ? 'Approved post-event media or guest uploads are available from the same occasion entry.'
        : 'Gallery access will appear only when hosts open it for this guest context.',
    metricLabel: 'items',
    metricValue: String(input.summary.galleryItemCount),
  });

  if (input.summary.sharedPlanningItemCount > 0) {
    tasks.push({
      type: 'shared_planning',
      status: 'complete',
      required: false,
      target: 'shared_planning',
      label: 'Shared planning',
      summary:
        'Shared planning items are visible for this guest and stay scoped to their occasion context.',
      metricLabel: 'items',
      metricValue: String(input.summary.sharedPlanningItemCount),
    });
  }

  tasks.push({
    type: 'post_event',
    status: input.summary.phase === 'post_event' ? 'in_progress' : 'blocked',
    required: false,
    target: input.summary.returnTarget,
    label:
      input.summary.phase === 'post_event'
        ? 'Post-event return'
        : 'Return later',
    summary:
      input.summary.phase === 'post_event'
        ? 'The main event window has passed, so return visits shift toward approved follow-up content.'
        : 'This occasion keeps one stable return target so guests can come back without rebuilding context.',
    metricLabel: 'return target',
    metricValue: input.summary.returnTarget.replace('_', ' '),
  });

  return tasks;
}

export function resolveGuestDeepLinkTarget(input: {
  requestedTarget?: string | null;
  summary: GuestJourneySummaryRecord;
}): GuestDeepLinkResolution {
  const hasRequestedTarget =
    input.requestedTarget !== null &&
    input.requestedTarget !== undefined &&
    input.requestedTarget.trim().length > 0;
  const requestedTarget = normalizeTarget(input.requestedTarget);

  if (!requestedTarget) {
    return {
      target: input.summary.recommendedTarget,
      didFallback: hasRequestedTarget,
      reason: hasRequestedTarget ? 'invalid_requested_target' : 'recommended',
    };
  }

  if (requestedTarget === 'home' || requestedTarget === 'access') {
    return {
      target: requestedTarget,
      didFallback: false,
      reason: 'requested',
    };
  }

  if (input.summary.availableTargets.includes(requestedTarget)) {
    return {
      target: requestedTarget,
      didFallback: false,
      reason: 'requested',
    };
  }

  return {
    target: input.summary.recommendedTarget,
    didFallback: true,
    reason: requestedTarget
      ? 'unavailable_requested_target'
      : 'invalid_requested_target',
  };
}

// --- Adaptive guest entry helpers ---
//
// These helpers determine which entry flow steps can be skipped for the common
// case (single actor, known language, no household selection needed). The goal
// is to route simple guests to RSVP/action surface as quickly as possible.

export interface GuestEntryContext {
  resolvedActorCount: number;
  requiresIdentityConfirmation: boolean;
  knownLocaleCode: string | null;
  localeAmbiguous: boolean;
  responseScope: 'actor' | 'household' | 'group' | 'delegated';
  householdMemberCount: number;
}

export interface GuestEntryStepDecision {
  skipIdentitySelection: boolean;
  skipLanguageStep: boolean;
  skipHouseholdSelection: boolean;
  recommendedEntryTarget: GuestJourneyTarget;
  skipReason: string[];
}

export function resolveGuestEntrySteps(
  context: GuestEntryContext,
  summary: GuestJourneySummaryRecord,
): GuestEntryStepDecision {
  const skipIdentitySelection =
    context.resolvedActorCount === 1 && !context.requiresIdentityConfirmation;

  const skipLanguageStep =
    context.knownLocaleCode !== null && !context.localeAmbiguous;

  const skipHouseholdSelection =
    context.responseScope === 'actor' || context.householdMemberCount <= 1;

  const skipReason: string[] = [];
  if (skipIdentitySelection) {
    skipReason.push(
      'Token resolved exactly one actor with no identity confirmation required.',
    );
  }
  if (skipLanguageStep) {
    skipReason.push('Language preference is already known and unambiguous.');
  }
  if (skipHouseholdSelection) {
    skipReason.push(
      'Household selection is not relevant for this response scope.',
    );
  }

  return {
    skipIdentitySelection,
    skipLanguageStep,
    skipHouseholdSelection,
    recommendedEntryTarget: summary.recommendedTarget,
    skipReason,
  };
}

// --- Session-expiry recovery types ---
//
// Guests whose session expires or is revoked mid-flow get a proper recovery
// path instead of a broken or empty screen.

export type GuestSessionRecoveryReason =
  | 'expired_mid_flow'
  | 'revoked_mid_flow'
  | 'new_link_available'
  | 're_entry_possible';

export interface GuestSessionRecoveryPath {
  reason: GuestSessionRecoveryReason;
  canRecover: boolean;
  recoveryLabel: string;
  recoveryHref: string | null;
  explanation: string;
}

export function resolveGuestSessionRecoveryPath(input: {
  accessStatus: GuestAccessLifecycleStatus;
  occasionSlug: string;
  hasReEntryToken: boolean;
  newLinkHref: string | null;
}): GuestSessionRecoveryPath {
  const newLinkHref = normalizeRecoveryHref(input.newLinkHref);
  const occasionPathSegment = encodeURIComponent(input.occasionSlug.trim());

  if (input.accessStatus === 'revoked') {
    return {
      reason: 'revoked_mid_flow',
      canRecover: newLinkHref !== null,
      recoveryLabel: newLinkHref
        ? 'Use the latest invite link'
        : 'Contact the occasion host',
      recoveryHref: newLinkHref,
      explanation:
        'Your access to this occasion was revoked. If you have a new invite link from the host, use that to re-enter.',
    };
  }

  if (input.accessStatus === 'expired') {
    if (newLinkHref) {
      return {
        reason: 'new_link_available',
        canRecover: true,
        recoveryLabel: 'Use the latest invite link',
        recoveryHref: newLinkHref,
        explanation:
          'Your session expired, but a new invite link is available. Open the latest link to continue.',
      };
    }

    if (input.hasReEntryToken) {
      return {
        reason: 're_entry_possible',
        canRecover: true,
        recoveryLabel: 'Request a new link',
        recoveryHref: `/${occasionPathSegment}/re-entry`,
        explanation:
          'Your session expired. You can request a new access link by re-entering your contact details.',
      };
    }

    return {
      reason: 'expired_mid_flow',
      canRecover: false,
      recoveryLabel: 'Contact the occasion host',
      recoveryHref: null,
      explanation:
        'Your session expired and no automatic recovery path is available. Contact the occasion host to receive a fresh invite.',
    };
  }

  return {
    reason: 're_entry_possible',
    canRecover: true,
    recoveryLabel: 'Continue',
    recoveryHref: `/${occasionPathSegment}`,
    explanation: 'Your session is still active. Continue to the occasion.',
  };
}
